import React, { useEffect } from "react";

import { AddRounded, CloseRounded, DeleteRounded, SaveRounded } from "@mui/icons-material";
import { Box, DialogContent, Divider } from "@mui/material";
import { httpsCallable } from "firebase/functions";

import useFirebase from "../../../../../config/context/firebaseContext";
import useModal from "../../../../../config/context/modalContext";
import { Dev } from "../../../../../config/util/consoleUtil";

import SelectMain from "../../../../../components/InputMain/SelectMain";
import TextMain from "../../../../../components/InputMain/TextMain";
import { WideDialog } from "../../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../../components/MainSolidButton";
import { ModalTransition } from "../../../../../components/ModalTransition";
import PageHeader from "../../../../../components/PageHeader";

import { BusScheduleInfoType } from "../../../../../types/bus";
import { ModalBaseType } from "../../../../../types/common";
import TimeItem from "./components/TimeItem";

type ModalWriteScheduleProps = ModalBaseType & {
  schedule?: BusScheduleInfoType;
  id?: string;
};

export type CustomItemSchedule = {
  value: string;
  ru: boolean;
  terminal: boolean;
  company: string;
  id: string;
};

export default function ModalWriteSchedule({ onClose, schedule, id }: ModalWriteScheduleProps) {
  const { pushAlert } = useModal();
  const { functions } = useFirebase();

  const [open, setOpen] = React.useState(true);

  const [idField, setIdField] = React.useState(id || "");

  const [nameField, setNameField] = React.useState(schedule?.name || "");
  const [shortNameField, setShortNameField] = React.useState(schedule?.shortName || "");
  const [colorField, setColorField] = React.useState(schedule?.color || "");

  const [scheduleField, setScheduleField] = React.useState<CustomItemSchedule[]>([]);

  const [loading, setLoading] = React.useState(false);
  const [loadingDelete, setLoadingDelete] = React.useState(false);

  const processSchedule = () => {
    if (schedule?.schedule) {
      let arrayOfSchedule: CustomItemSchedule[] = [];

      Object.keys(schedule.schedule).map((hour, hIndex) => {
        Object.keys(schedule.schedule[hour]).map((minute, mIndex) => {
          const itHourValue = `0${hour}`.slice(-2);
          const itMinuteValue = `0${minute}`.slice(-2);
          const itTime = `${itHourValue}:${itMinuteValue}`;
          arrayOfSchedule.push({
            value: itTime,
            ru: !!schedule.schedule[hour][minute]?.ru,
            terminal: !!schedule.schedule[hour][minute]?.terminal,
            company: schedule.schedule[hour][minute]?.company || "",
            id: `${hour}${minute}-${hIndex}${mIndex}`,
          });
        });
      });

      setScheduleField(arrayOfSchedule);
    }
  };

  const handleWrite = async (operation: "create" | "update" | "delete") => {
    try {
      operation === "delete" ? setLoadingDelete(true) : setLoading(true);
      const writeConfig = httpsCallable(functions, "writeRemoteConfigSchedules");

      const parsedSchedule: BusScheduleInfoType["schedule"] = {};

      scheduleField.map((item) => {
        const hour = String(parseInt(item.value.split(":")[0]));
        const minute = String(parseInt(item.value.split(":")[1]));
        let props: any = {};

        if (item.ru) props.ru = true;
        if (item.terminal) props.terminal = true;
        if (item.company) props.company = item.company;

        if (hour in parsedSchedule) {
          parsedSchedule[hour][minute] = props;
        } else {
          parsedSchedule[hour] = { [minute]: props };
        }
      });

      const payload = {
        operation,
        id: id || idField,
        schedule: {
          name: nameField,
          shortName: shortNameField,
          color: colorField,
          schedule: parsedSchedule,
        },
      } as {
        id: string;
        operation: string;
        schedule: BusScheduleInfoType;
      };

      const response = await writeConfig(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Configurações atualizadas",
      });

      handleClose(true);
    } catch (error) {
      console.log(...new Dev("handleWrite", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados`,
      });
    } finally {
      setLoading(false);
      setLoadingDelete(false);
    }
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  const handleDelete = () => {
    pushAlert({
      isConfirm: true,
      type: "delete",
      onConfirm: async () => {
        handleWrite("delete");
      },
    });
  };

  useEffect(() => {
    processSchedule();
  }, []);

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={schedule ? "Editar" : "Criar Itinerário"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <Box>
          <Box>
            <TextMain
              disabled={!!schedule}
              textColor={"regular"}
              name="ID"
              onChange={setIdField}
              value={idField}
            />

            <Box mb={2}>
              <SelectMain
                name="Cor"
                value={colorField || ""}
                options={[
                  { name: "Direto", value: "direto" },
                  { name: "Inverso", value: "inverso" },
                  { name: "Reitoria", value: "reitoria" },
                  { name: "CeT", value: "cet" },
                ]}
                onChange={(value) => setColorField(value)}
              />
            </Box>

            <TextMain
              textColor={"regular"}
              name="Título"
              onChange={setNameField}
              value={nameField}
            />

            <TextMain
              textColor={"regular"}
              name="Nome Curto"
              onChange={setShortNameField}
              value={shortNameField}
            />
          </Box>

          <Box>
            {scheduleField.map((item) => (
              <TimeItem key={item.id} item={item} setter={setScheduleField} />
            ))}
          </Box>

          <Box my={2}>
            <MainSolidButton
              text={"Adicionar Hora"}
              icon={<AddRounded />}
              onClick={() => {
                setScheduleField([
                  ...scheduleField,
                  {
                    company: "",
                    ru: false,
                    terminal: false,
                    value: "",
                    id: String(new Date().getTime()),
                  },
                ]);
              }}
            />
          </Box>

          <Box py={2}>
            <Divider />
          </Box>

          <Box my={2}>
            <MainSolidButton
              loading={loading}
              disabled={loading || loadingDelete}
              text={schedule ? "Salvar" : "Criar"}
              icon={schedule ? <SaveRounded /> : <AddRounded />}
              onClick={() => handleWrite(schedule ? "update" : "create")}
            />
          </Box>

          {!!schedule && (
            <Box my={2}>
              <MainSolidButton
                color="error"
                loading={loadingDelete}
                text={"Deletar"}
                disabled={loading || loadingDelete}
                icon={<DeleteRounded />}
                onClick={handleDelete}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
